<!-- src/pages/MyPage.vue -->
<script setup>
definePage({ meta: { layout: 'mypage' } });
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import articleApi from '@/api/ArticleApi';
import { formatCustomDate } from '@/composables/functions';
import ConfirmComponent from '@/components/common/Confirm.vue';
import IconFile from '@/components/svg/IconFile.vue';
import AlertComponent from '@/components/common/Alert.vue';
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

const creditInfoRef = ref(null);

const store = useStore();
const route = useRoute();
const router = useRouter();
const myInfoStore = useMyInfoStore();

const id = ref(null);
const boardName = ref(null);

const boardMapping = {
  counseling: 2508,
  inquiry: 2336,
  eventinquiry: 2522,
};

const listData = ref({});
const comment = ref({});
const memberId = ref({});
const categoryCtn = ref({});
const dialogAlert = ref(null);

onMounted(async () => {
  id.value = route.params?.id;
  boardName.value = route.params?.boardName;
  await getView();
});

const getView = async () => {
  try {
    const result = await articleApi().get(boardMapping[boardName.value], id.value, {});

    if (!result) {
      router.back();
      return;
    }
    listData.value = result?.data;
    comment.value = listData.value.comments[0];
    categoryCtn.value = listData.value.boardCategory.categoryCtn;
    // memberInfo.value = store.getters[""]
    memberId.value = store.getters['user/id'];
  } catch (error) {
    alert(error);
  }
};

const goList = () => {
  router.push({
    path: '/mypage/inquiryList',
    query: { boardName: boardName.value },
  });
};

const delConfirm = async () => {
  showDialogConfirm('삭제하시겠습니까?', '삭제된 정보는 다시 볼 수 없습니다.', '삭제', async () => {
    await del();
  });
};

const del = async () => {
  try {
    const result = await articleApi().delete(boardMapping[boardName.value], id.value, {});
    if (result?.data === true) {
      showDialogAlert(
        '문의가 삭제되었습니다',
        '',
        `/mypage/inquiryList?boardName=${boardName.value}`,
      );
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || '삭제 중 오류가 발생했습니다.';
    showDialogAlert(errorMessage);
  }
};
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);

const dialogConfrim = ref(null);

const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달

  dialogConfrim.value.openConfirmDialog();
};

const showDialogAlert = (title, text = '', nextUrl = '') => {
  dialogTitle.value = title;
  dialogText.value = text;
  dialogNextUrl.value = nextUrl;
  dialogAlert.value.openAlertDialog();
};

const newText = 'new'; // 표시할 텍스트

const isNew = computed(() => {
  const createdAt = new Date(listData.value.createdAt);
  const today = new Date();

  // 날짜를 비교하기 위해 시간 부분을 0으로 설정
  createdAt.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return createdAt.getTime() === today.getTime(); // 날짜 비교
});

// 상태에 따른 텍스트 반환
const getStatusText = (status) => {
  const statusTextMap = {
    PENDING: '미확인',
    IN_PROGRESS: '답변대기',
    WRITING: '답변작성중',
    COMPLETED: '답변완료',
  };
  return statusTextMap[status] || '';
};

/** 뒤로가기: 항상 문의 목록으로 */
const handleBack = () => {
  router.replace({
    path: '/mypage/inquiryList',
    query: { boardName: boardName.value || 'inquiry' },
  });
};

const isWebView = ref(false);
onMounted(() => {
  isWebView.value = !!window.ReactNativeWebView;
});

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);
const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};
const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};
watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
  },
);

const isLoggedIn = computed(() => {
  const uid = store.getters['user/id'];
  const uname = store.getters['user/name'];
  try {
    const u = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(uid || uname || u?.name);
  } catch {
    return !!(uid || uname);
  }
});
const displayName = computed(() =>
  isLoggedIn.value ? myInfoStore?.displayName || store.getters['user/name'] || '' : '',
);
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');
const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) router.push('/main');
};
const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({ path: '/auth/login', query: { to: route.fullPath } });
  closeMypageMenu();
};
const isMenuActive = (menuLink) => {
  const p = route.path;
  if (menuLink.name === '낭만분석')
    return (
      p.startsWith('/saju-service') ||
      p.startsWith('/saju-result') ||
      p.startsWith('/saju-insight') ||
      p.startsWith('/analysis')
    );
  return p.startsWith(menuLink.basePath || menuLink.link);
};
const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') router.push(menu.link);
  closeMypageMenu();
};
const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (e) {
    console.error('Logout error:', e);
  }
};
</script>

<template>
  <div class="my-info-wrap inquiry-view-page">
    <div class="inquiry-detail-header">
      <button class="inquiry-detail-header__back" @click="handleBack">
        <IconHeaderBack />
      </button>
      <div class="inquiry-detail-header__title">문의내역</div>
      <div v-if="!isWebView" class="inquiry-detail-header__right">
        <button class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>홈
            </button>
            <template v-if="isLoggedIn"
              ><div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                ><span class="txt2">행복한 하루 되세요</span>
              </div></template
            >
            <template v-else
              ><div class="txt-box">
                <span class="txt1">게스트</span><span class="txt2">로그인 후 이용해주세요</span>
              </div></template
            >
            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />
            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</router-link
                >
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                  >{{ menu.name }}</a
                >
              </template>
            </div>
            <template v-if="isLoggedIn"
              ><MypageLnbTabMenu /><button class="btn-xs line" @click="handleLogout">
                로그아웃
              </button></template
            >
            <template v-else
              ><button class="btn-xs line login-btn" @click="handleLogin">로그인</button></template
            >
          </div>
        </div>
      </div>
    </Teleport>
    <div class="my-tit-box com-bg-my-box h-small">
      <div class="tit fnt-big">내역보기상세</div>
      <div class="path">
        <ul>
          <li>홈</li>
          <li>마이페이지</li>
          <li>문의/신청</li>
          <li>내역보기상세</li>
        </ul>
      </div>
    </div>
    <div class="myinfo-box com-bg-my-box">
      <div class="my-inquiry-user">
        <div class="top-box">
          <div class="tit-box">
            <span class="tit">[{{ categoryCtn }}]</span>
            <span class="txt">{{ listData.articleTtl }}</span>
            <span class="art-icon-box">
              <span class="txt NEW" v-if="isNew">{{ newText }}</span>
            </span>
          </div>
          <div class="ans-box">
            <span :class="{ 'fnt-completed': listData.inquiryStatus === 'COMPLETED' }">
              {{ getStatusText(listData.inquiryStatus) }}</span
            >
          </div>
        </div>
        <div class="date-box">{{ formatCustomDate(listData.createdAt) }}</div>
        <div class="gap-line-01"></div>
        <div class="cont-box">
          <div
            class=""
            style="white-space: pre-wrap; word-wrap: break-word; word-break: break-all"
            v-html="listData.articleCtn"
          />
        </div>
        <div class="alert-box" v-if="listData.notifyMobileNo || listData.notifyEmail">
          <span class="tit">답변 알림</span>
          <span class="txt">
            <span> 휴대폰 번호 : {{ listData.notifyMobileNo }} / </span>
            <span> 이메일 : {{ listData.notifyEmail }} </span>
          </span>
        </div>
      </div>
    </div>
    <div
      class="myinfo-box com-bg-my-box mg-none inquiry-reply-box"
      v-if="listData.inquiryStatus === 'COMPLETED'"
    >
      <div class="my-inquiry-admin">
        <div class="top-box">
          <div class="ans-name">만세력 운영진 답변</div>
          <div class="btn-box">
            <!-- <VBtn type="submit" class="custom-btn h-sm" @click="handleSubmit"> 재문의하기 </VBtn> -->
          </div>
        </div>
        <div class="date-box">{{ formatCustomDate(comment?.createdAt) }}</div>
        <div class="cont-box">
          <div
            class=""
            style="white-space: pre-wrap; word-wrap: break-word; word-break: break-all"
            v-html="comment?.name"
          />
        </div>
        <!-- <div class="file-box">
          <span class="tit">첨부파일</span>
          <span class="txt">
            <button class="btn-icon"><IconFile /> 낭만만세력 운영가이드v1.jpg</button>
          </span>
        </div> -->
      </div>
    </div>
    <div
      class="myinfo-box com-bg-my-box mg-none inquiry-reply-box"
      v-if="listData.inquiryStatus != 'COMPLETED'"
    >
      <div class="my-inquiry-admin">
        <div class="top-box">
          <div class="ans-name">낭만만세력 운영진</div>
        </div>
        <div class="cont-box">
          <div class="reply-default-box">
            자연의 이치를 담은 천문역법 서비스 "낭만만세력"을 이용해 주셔서 감사합니다.<br /><br />

            아직 답변이 등록되지 않았습니다. 순차적으로 처리 중 입니다. <br />
            조금만 기다려주세요, "낭만만세력"을 이용해 주셔서 다시 한번 감사드립니다.<br />

            낭만만세력 고객지원 드림
          </div>
        </div>
      </div>
    </div>

    <div class="gap-xxlg"></div>
    <div class="com-btn-box">
      <VBtn color="info" @click="goList" variant="outlined" class="custom-btn"> 목록 </VBtn>
      <VBtn type="submit" color="info" class="custom-btn" @click="delConfirm()"> 삭제 </VBtn>
    </div>
  </div>

  <ConfirmComponent
    ref="dialogConfrim"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
  />
</template>
<style lang="scss">
.v-dialog-alert .alert-wrap .tit-box {
  gap: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}
.mypage-body .board-inquiry-box {
  display: none;
}
.mypage-body .board-tit-box {
  display: none;
}
.fnt-completed {
  color: var(--orange-300-main, #eb4c10);
  text-align: right;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 133.333% */
  letter-spacing: -0.375px;
}
.mypage-body .my-inquiry-user .art-icon-box .NEW {
  display: flex;
  height: 18px;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: rgba(255, 180, 1, 0.1);
  color: #ffb401;
  text-align: center;
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px; /* 120% */
}
.reply-default-box {
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  margin-top: 16px;
  padding-top: 44px;
  border-top: 1px solid #ebebeb;
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px; /* 173.333% */
  letter-spacing: -0.375px;
}

.inquiry-detail-header {
  display: none;
}

@media (max-width: 768px) {
  .my-info-wrap.inquiry-view-page {
    background: #fff;
    min-height: 100dvh;
    padding-top: calc(52px + var(--safe-area-top, 0px));
    padding-bottom: var(--safe-area-bottom, 0px);
  }

  .inquiry-detail-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    height: calc(52px + var(--safe-area-top, 0px));
    padding-top: var(--safe-area-top, 0px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-bottom: 1px solid #ebebeb;

    .inquiry-detail-header__back {
      position: absolute;
      left: 16px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }

    .inquiry-detail-header__title {
      color: #333;
      font-family: Pretendard;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
      text-align: center;
    }

    .inquiry-detail-header__right {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
    }

    .btn-mypage {
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      .icon-mypage {
        display: flex;
        background: url('@manseImages/icon-mypage.png') center center no-repeat;
        background-size: 32px auto;
        width: 32px;
        height: 32px;
        border-radius: 100%;
        font-size: 0;
      }
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }
  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;
      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }
        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }
      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;
        .txt1 {
          font-weight: 700;
        }
        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }
      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;
        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;
          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }
          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }
      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          background: #f5f5f5;
        }
        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);
          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }

  .my-tit-box.com-bg-my-box {
    display: none !important;
  }

  .my-info-wrap .myinfo-box.com-bg-my-box {
    width: 100%;
    max-width: 100%;
    padding: 16px;
    box-sizing: border-box;
    margin-left: 0;
    margin-right: 0;

    .my-inquiry-user,
    .my-inquiry-admin {
      width: 100%;

      .top-box {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 8px;
      }

      .tit-box {
        align-items: flex-start;
      }

      .txt {
        margin-right: 0;
      }

      &.my-inquiry-user {
        .top-box {
          // display: block;
        }

        .tit-box {
          display: flex;
          align-items: flex-start;
          gap: 6px;
        }

        .tit-box > .tit {
          flex-shrink: 0;
          line-height: 24px;
        }

        .tit-box > .txt {
          flex: 1;
          min-width: 0;
          display: block;
          margin: 0;
          line-height: 24px;
          white-space: normal;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        .tit-box .art-icon-box {
          flex-shrink: 0;
          align-self: flex-start;
          margin-left: 0;
        }

        .ans-box {
          display: flex;
          justify-content: flex-end;
          white-space: nowrap;
          .fnt-completed {
            line-height: 24px;
          }
        }
      }

      .cont-box {
        margin-bottom: 0;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        word-wrap: break-word;
        word-break: break-word;
        overflow-wrap: break-word;

        > div {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;

          img {
            max-width: 100%;
            height: auto;
          }

          table {
            width: 100%;
            max-width: 100%;
            table-layout: fixed;
          }
        }
      }
    }
  }

  .my-info-wrap .myinfo-box.com-bg-my-box.mg-none {
    margin-top: 8px;
  }

  .my-info-wrap .myinfo-box.com-bg-my-box.inquiry-reply-box {
    background: transparent;
    padding: 0 16px !important;

    .my-inquiry-admin {
      display: flex;
      flex-direction: column;
      gap: 20px;
      background: #f8f8f8;
      border-radius: 16px;
      padding: 20px;
      box-sizing: border-box;

      .top-box {
        display: block;
      }

      .ans-name {
        color: #eb4c10;
        font-family: Pretendard;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
      }

      .date-box {
        order: 3;
        margin: 0;
        color: #757575;
        font-family: Pretendard;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px;
      }

      .cont-box {
        order: 2;
        margin: 0;
      }

      .cont-box > div {
        color: #333;
        font-family: Pretendard;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .reply-default-box {
        border-top: 0;
        padding-top: 0;
        margin-top: 0;
      }
    }
  }

  .com-btn-box {
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
    display: flex;
    gap: 8px;

    .custom-btn {
      flex: 1;
    }
  }
}
</style>
